import { useEffect, useMemo, useRef, useState } from "react";

import MoreFilter from "@cleanup/components/AdvancedFilters/MoreFilter/MoreFilter";
import DateFilter from "@cleanup/components/AdvancedFilters/DateFilter/DateFilter";
import StringFilter from "@cleanup/components/AdvancedFilters/StringFilter/StringFilter";
import NumericFilter from "@cleanup/components/AdvancedFilters/NumericFilter/NumericFilter";
import BooleanFilter from "@cleanup/components/AdvancedFilters/BooleanFilter/BooleanFilter";
import MultiStringFilter from "@cleanup/components/AdvancedFilters/MultiStringFilter/MultiStringFilter";
import MultiCheckboxFilter from "@cleanup/components/AdvancedFilters/MultiCheckboxFilter/MultiCheckboxFilter";
import MultiSelectAutocompleteFilter from "@cleanup/components/AdvancedFilters/MultiSelectAutocompleteFilter/MultiSelectAutocompleteFilter";
import SntLink from "@/components/ReactBootstrap/SntLink";
import { AdvanceFilter } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { SntCollapse } from "@cleanup/widgets/SntCollapse";
import { ActionEnum } from "@/components/SntTableViewCard/ActionEnum";
import momentTZ from "@/constants/SensolusMoment";
import BoundFilter from "@cleanup/components/AdvancedFilters/BoundFilter/BoundFilter";
import ActionTool from "@/containers/AdminLayout/Toolbar/ActionTool";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterTypeSettings {
  [key: string]: unknown;
}

interface SearchField {
  _idx: string;
  key: string;
  label?: string;
  description: string;
  filterType: string;
  filterTypeSettings: FilterTypeSettings;
  [key: string]: unknown;
}

interface CategoryDescriptor {
  categoryID: string;
  categoryLabel: string;
  searchFields: SearchField[];
}

interface FilterParam {
  filterKey: string;
  filterType: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: Record<string, unknown>;
  notFilter?: boolean;
  [key: string]: unknown;
}

interface BasicQuery {
  searchQueryType?: string;
  query: FilterParam[];
}

interface Action {
  type: string;
  idx: string | null;
}

interface ForceFilterAction {
  filterKeys: string[];
  action: string;
  query?: (string | number | boolean | null)[];
}

interface ActionToolOption {
  id: string;
  type?: string;
  title?: string;
  icon?: React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
  isAlwayShow?: boolean;
}

interface FilterListProps {
  isVisibleAddFilterBtn?: boolean;
  restrictedFilters?: string[];
  onChange?: (basicQuery: BasicQuery, action: Action) => void;
  data: {
    descriptors: CategoryDescriptor[];
    appliedFilterSettings?: FilterParam[];
    filtersVisibleAtInit?: string[];
  };
  onResetFilterData?: () => void;
  filterListRef?: React.MutableRefObject<{ clearFilter: () => void } | undefined>;
  disabled?: boolean;
  totalElement?: string | number;
  filterToolList?: ActionToolOption[];
  handleTool?: (tool: unknown) => void;
  deviceCategorySuggestion?: string;
  forceFilterAction?: ForceFilterAction | null;
  setForceFilterAction?: (action: ForceFilterAction | null) => void;
  isDisableCollapseOnMobile?: boolean;
}

interface ComponentData {
  descriptor: SearchField;
  data: FilterParam;
}

const FilterList: React.FC<FilterListProps> = ({
  isVisibleAddFilterBtn = true,
  restrictedFilters = [],
  onChange,
  data = { descriptors: [], appliedFilterSettings: [], filtersVisibleAtInit: [] },
  onResetFilterData,
  filterListRef,
  disabled = false,
  totalElement,
  filterToolList,
  handleTool,
  deviceCategorySuggestion,
  forceFilterAction,
  setForceFilterAction,
  isDisableCollapseOnMobile,
}) => {
  const [filtersVisible, setFiltersVisible] = useState<string[]>([]);
  const [components, setComponents] = useState<ComponentData[]>([]);

  const action = useRef<Action>({ type: ActionEnum.EMPTY, idx: null });
  const mapDescriptors = useRef<Record<string, SearchField>>({});
  const appliedFilterSettings = useRef<Record<string, FilterParam>>({});
  const previousComponentRender = useRef<JSX.Element[]>([]);
  const filterRef = useRef<HTMLAnchorElement>(null);

  const _getDescriptorByFilter = (_filter: FilterParam): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      const _descriptor = mapDescriptors.current[_idx];

      if (
        (_descriptor.key === _filter.key ||
          _descriptor.key === _filter.filterKey) &&
        _descriptor.searchType === _filter.searchType
      ) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDescriptorByKey = (_key: string): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      if (mapDescriptors.current[_idx].key === _key) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDescriptorByIdx = (idx: string): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      if (mapDescriptors.current[_idx]._idx === idx) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDefaultApplyFilter = (idx: string): FilterParam => {
    const descriptor = _getDescriptorByIdx(idx);
    if (descriptor && descriptor.categoryId && descriptor.key && descriptor.filterType && descriptor.searchType) {
      return {
        categoryId: descriptor.categoryId as string,
        filterKey: descriptor.key,
        filterType: descriptor.filterType,
        searchType: descriptor.searchType as string,
        notFilter: false,
      };
    }
    return {} as FilterParam;
  };

  const _sortByArray = (descArray: string[], conditionArr: string[]): string[] => {
    const arr = Array.from(descArray);
    const result: string[] = [];
    for (let i = 0; i < conditionArr.length; i++) {
      const index = arr.indexOf(conditionArr[i]);
      if (index !== -1) {
        result.push(conditionArr[i]);
        arr.splice(index, 1);
      }
    }
    return [...result, ...arr];
  };

  const loadDescriptors = () => {
    for (let i = 0, len = data.descriptors.length; i < len; i++) {
      const _category = data.descriptors[i];
      const _searchFields = _category.searchFields || [];

      for (let j = 0; j < _searchFields.length; j++) {
        const _idx = "descriptor_" + i + "_" + j;
        const _field = _searchFields[j];

        if (restrictedFilters.indexOf(_field.key) !== -1) {
          _searchFields.splice(j, 1);
          j--;
          continue;
        }
        _field._idx = _idx;
        mapDescriptors.current[_idx] = _field;
      }
    }
    loadAppliedFilter();
  };

  const loadAppliedFilter = () => {
    const _filtersVisible: string[] = [];
    const list = data.appliedFilterSettings || [];

    if (list.length > 0) {
      for (let i = 0, len = list.length; i < len; i++) {
        const _filter = list[i];

        const _descriptor = _getDescriptorByFilter(_filter);
        if (_descriptor) {
          appliedFilterSettings.current[_descriptor._idx] = _filter;
          _filtersVisible.push(_descriptor._idx);
        }
      }
      setFiltersVisible(_filtersVisible);
    }

    if (_filtersVisible.length === 0) {
      if (
        !data.filtersVisibleAtInit ||
        data.filtersVisibleAtInit.length === 0 ||
        Object.keys(mapDescriptors.current).length === 0
      ) {
        return;
      }

      const _filtersVisibleAtInit = data.filtersVisibleAtInit || [];
      for (let i = 0, len = _filtersVisibleAtInit.length; i < len; i++) {
        const _key = _filtersVisibleAtInit[i];

        const _descriptor = _getDescriptorByKey(_key);
        if (_descriptor) {
          _filtersVisible.push(_descriptor._idx);
        }
      }
      appliedFilterSettings.current = {};
      setFiltersVisible(_filtersVisible);
    }
  };

  const loadFilters = (filters = filtersVisible) => {
    const _data: ComponentData[] = [];
    for (let i = 0; i < filters.length; i++) {
      const _key = filters[i];
      appliedFilterSettings.current[_key] = appliedFilterSettings.current[_key]
        ? appliedFilterSettings.current[_key]
        : _getDefaultApplyFilter(_key);

      _data.push({
        descriptor: mapDescriptors.current[_key],
        data: appliedFilterSettings.current[_key],
      });
    }
    setComponents(_data);
    submitFilter();
  };

  useEffect(() => {
    if (filterListRef) {
      filterListRef.current = {
        clearFilter: function () {
          if (filterRef.current) {
            filterRef.current.click();
          }
        },
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    action.current = {
      type: ActionEnum.EMPTY,
      idx: null,
    };
    loadDescriptors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersVisible]);

  useEffect(() => {
    if (!forceFilterAction) return;

    let existedFilter: FilterParam | null = null;
    for (const key in appliedFilterSettings.current) {
      if (
        appliedFilterSettings.current[key].filterKey ===
        forceFilterAction.filterKeys[0]
      ) {
        existedFilter = appliedFilterSettings.current[key];
      }
    }

    if (!existedFilter) {
      for (const key in mapDescriptors.current) {
        if (
          mapDescriptors.current[key].key === forceFilterAction.filterKeys[0]
        ) {
          appliedFilterSettings.current[key] = _getDefaultApplyFilter(key);
          existedFilter = appliedFilterSettings.current[key];
          break;
        }
      }
    }

    if (existedFilter) {
      existedFilter.filterValue = existedFilter.filterValue
        ? { ...existedFilter.filterValue }
        : {};
      const { filterValue, filterType } = existedFilter;
      const { selectedValues = [], operator } = (filterValue || {}) as { selectedValues?: (string | number)[]; operator?: string };
      const { action: filterAction, query } = forceFilterAction;

      if (filterAction === "FILTER_FOR_VALUE") {
        if (filterType === "MULTI_STRING") {
          filterValue.searchText = query?.length ? String(query[0] || "") : "";
        } else if (filterType === "STRING") {
          if (query?.length) {
            filterValue.searchText = String(query[0] || "");
          } else {
            filterValue.searchText = "";
            filterValue.includeEmpty = true;
            filterValue.includeNoEmpty = false;
          }
        } else if (filterType === "SPATIAL") {
          if (!query?.length) {
            filterValue.includeEmpty = true;
            filterValue.includeNoEmpty = false;
          }
        } else if (filterType === "BOOLEAN") {
          if (query?.length) {
            filterValue.naOnly = false;
            filterValue.value = query[0] === "true" || query[0] === true;
          } else {
            filterValue.value = null;
            filterValue.naOnly = true;
          }
        } else if (filterType === "NUMERIC") {
          existedFilter.notFilter = false;
          if (query?.length) {
            filterValue.eq = query[0];
            filterValue.operator = "EQ";
          } else {
            filterValue.eq = null;
            filterValue.operator = "NULL";
          }
        } else if (filterType === "DATE") {
          existedFilter.notFilter = false;

          filterValue.intervalSign = null;
          filterValue.lastUnit = null;
          filterValue.lastValue = null;
          filterValue.relativeEndDate = null;
          filterValue.relativeStartDate = null;
          filterValue.absoluteStartDate = null;
          filterValue.absoluteEndDate = null;
          if (query?.length) {
            filterValue.absoluteStartDate = momentTZ(query[0] as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(query[0] as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else {
            filterValue.dateFilterType = "NULL";
          }
        } else if (filterType === "MULTI_SELECT_CHECKBOX") {
          if (!query) {
            filterValue.operator = "NULL";
            filterValue.selectedValues = [];
          } else {
            filterValue.selectedValues = query || [];
          }
        } else {
          filterValue.selectedValues = query || [];
        }
      } else if (filterAction === "FILTER_OUT_VALUE") {
        if (filterType === "MULTI_SELECT_CHECKBOX") {
          if (!query) {
            filterValue.operator = "NOT_NULL";
            filterValue.selectedValues = [];
          } else if (selectedValues.length === 0) {
            filterValue.operator = "NOT_IN";
            filterValue.selectedValues = [...query];
          } else if (operator === "NOT_IN") {
            filterValue.selectedValues = [
              ...new Set([...selectedValues, ...query]),
            ];
          } else if (!operator || operator === "IN") {
            filterValue.selectedValues = (filterValue.selectedValues as (string | number)[]).filter(
              (item) => !forceFilterAction.query?.includes(item)
            );
            if ((filterValue.selectedValues as (string | number)[]).length === 0) {
              filterValue.operator = "NOT_IN";
              filterValue.selectedValues = [...query];
            }
          } else {
            filterValue.operator = "NOT_IN";
            filterValue.selectedValues = [...query];
          }
        } else if (filterType === "MULTI_SELECT_AUTO_COMPLETE") {
          if (query && query.length > 0) {
            if (selectedValues.length === 0) {
              existedFilter.notFilter = true;
              filterValue.selectedValues = [...query];
            } else if (existedFilter.notFilter) {
              filterValue.selectedValues = [
                ...new Set([...selectedValues, ...query]),
              ];
            } else {
              filterValue.selectedValues = (filterValue.selectedValues as (string | number)[]).filter(
                (item) => !query.includes(item)
              );
              if ((filterValue.selectedValues as (string | number)[]).length === 0) {
                existedFilter.notFilter = true;
                filterValue.selectedValues = [...query];
              }
            }
          } else {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = true;
            filterValue.selectedValues = [];
          }
        } else if (filterType === "STRING") {
          if (query && query.length > 0) {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = false;
            filterValue.searchText = String(query[0]);
          } else {
            existedFilter.notFilter = false;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = true;
            filterValue.searchText = "";
          }
        } else if (filterType === "SPATIAL") {
          if (query && query.length > 0) {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = false;
          } else {
            existedFilter.notFilter = false;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = true;
          }
        } else if (filterType === "BOOLEAN") {
          existedFilter.notFilter = true;
          if (query?.length) {
            filterValue.naOnly = false;
            filterValue.value = query[0] === "true" || query[0] === true;
          } else {
            filterValue.value = null;
            filterValue.naOnly = true;
          }
        } else if (filterType === "NUMERIC") {
          existedFilter.notFilter = true;
          if (query?.length) {
            filterValue.eq = query[0];
            filterValue.operator = "EQ";
          } else {
            filterValue.eq = null;
            filterValue.operator = "NULL";
          }
        } else if (filterType === "DATE") {
          existedFilter.notFilter = true;
          filterValue.intervalSign = null;
          filterValue.lastUnit = null;
          filterValue.lastValue = null;
          filterValue.relativeEndDate = null;
          filterValue.relativeStartDate = null;
          filterValue.absoluteStartDate = null;
          filterValue.absoluteEndDate = null;
          if (query?.length) {
            filterValue.absoluteStartDate = momentTZ(query[0] as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(query[0] as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else {
            filterValue.dateFilterType = "NULL";
          }
        }
      }
    }

    if (setForceFilterAction) {
      setForceFilterAction(null);
    }

    const _data: string[] = [];
    for (const key in appliedFilterSettings.current) {
      _data.push(key);
    }

    action.current = { type: ActionEnum.UPDATE_FILTER, idx: null };
    setFiltersVisible(_sortByArray(_data, filtersVisible));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceFilterAction]);

  const onChangeMoreFilter = (_data: string[]) => {
    const newApplyFilterSettings: Record<string, FilterParam> = {};
    for (let i = 0; i < _data.length; i++) {
      if (appliedFilterSettings.current[_data[i]]) {
        newApplyFilterSettings[_data[i]] =
          appliedFilterSettings.current[_data[i]];
      } else {
        newApplyFilterSettings[_data[i]] = _getDefaultApplyFilter(_data[i]);
      }
    }

    let hasLocationByBound = false;
    for (const key in newApplyFilterSettings) {
      const filter = newApplyFilterSettings[key];
      if (filter.filterKey === "locationByBound") {
        hasLocationByBound = true;
      }
    }
    if (!hasLocationByBound) {
      const event = new CustomEvent("bound_switch_react_change", {
        detail: { switchOn: false },
      });
      document.dispatchEvent(event);
    }

    appliedFilterSettings.current = newApplyFilterSettings;

    action.current = { type: ActionEnum.CHANGE_MORE_FILTER, idx: null };
    setFiltersVisible(_sortByArray(_data, filtersVisible));
  };

  const submitFilter = (settings = appliedFilterSettings.current) => {
    const list: FilterParam[] = [];

    for (let i = 0; i < filtersVisible.length; i++) {
      if (settings[filtersVisible[i]]) {
        list.push(settings[filtersVisible[i]]);
      } else {
        list.push(_getDefaultApplyFilter(filtersVisible[i]));
      }
    }
    if (list.length > 0) {
      const basicQuery: BasicQuery = {
        searchQueryType: "BASIC",
        query: list,
      };
      if (onChange) {
        onChange(basicQuery, action.current);
      }
    }
  };

  const componentsRender = useMemo(() => {
    const onUpdateSearchFilterItem = (_data: unknown) => {
      if (typeof _data === 'object' && _data !== null && 'data' in _data && (_data as { data: unknown }).data === null) {
        const idxData = _data as { idx: string; data: null };
        delete appliedFilterSettings.current[idxData.idx];
      } else {
        const _descriptor = _getDescriptorByFilter(_data as FilterParam);
        if (_descriptor) {
          appliedFilterSettings.current[_descriptor._idx] = _data as FilterParam;
        }
      }
      action.current = { type: ActionEnum.UPDATE_FILTER, idx: null };
      loadFilters();
    };

    if (action.current.type === ActionEnum.REMOVE_FILTER) {
      const _render = previousComponentRender.current.filter(
        (c) => c.key !== action.current.idx
      );
      previousComponentRender.current = _render;
      action.current = { type: ActionEnum.EMPTY, idx: null };
      return _render;
    }

    const render: JSX.Element[] = [];
    let locationByBoundComponent: JSX.Element | null = null;

    components.forEach((obj) => {
      const { descriptor, data } = obj;

      let component: JSX.Element | null = null;

      if (descriptor.filterType === "MULTI_STRING") {
        component = (
          <MultiStringFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "STRING") {
        component = (
          <StringFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "SPATIAL") {
        component = (
          <BoundFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "DATE") {
        component = (
          <DateFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "NUMERIC") {
        component = (
          <NumericFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "BOOLEAN") {
        component = (
          <BooleanFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "MULTI_SELECT_CHECKBOX") {
        component = (
          <MultiCheckboxFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            deviceCategorySuggestion={deviceCategorySuggestion}
          />
        );
      } else if (descriptor.filterType === "MULTI_SELECT_AUTO_COMPLETE") {
        component = (
          <MultiSelectAutocompleteFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      }

      if (descriptor.key === "locationByBound") {
        locationByBoundComponent = component;
      } else if (component) {
        render.push(component);
      }
    });

    if (locationByBoundComponent) {
      render.push(locationByBoundComponent);
    }

    previousComponentRender.current = render;
    return render;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, disabled, onChange]);

  return (
    <>
      <SntCollapse isDisableCollapseOnMobile={isDisableCollapseOnMobile}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ float: "left" }}>
            {componentsRender}
            {isVisibleAddFilterBtn && (
              <MoreFilter
                onChange={onChangeMoreFilter}
                descriptors={data.descriptors as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                disabled={disabled}
                data={filtersVisible}
              />
            )}
            {totalElement && (
              <AdvanceFilter className="text-sensolus">
                {totalElement}
              </AdvanceFilter>
            )}
            {onResetFilterData && !disabled ? (
              <AdvanceFilter>
                <SntLink
                  ref={filterRef}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onResetFilterData();
                  }}
                >
                  {translate(LanguageKey.clear_all)}
                </SntLink>
              </AdvanceFilter>
            ) : null}
          </div>
          {filterToolList && filterToolList.length > 0 && (
            <div
              style={{
                overflow: "hiden",
                float: "none",
                flex: "1 1 calc(20%)",
              }}
            >
              <AdvanceFilter className="w-100">
                <ActionTool
                  list={filterToolList}
                  onClick={handleTool}
                  group={undefined}
                />
              </AdvanceFilter>
            </div>
          )}
        </div>
      </SntCollapse>
    </>
  );
};

export default FilterList;
